import { getCurrencyForCountry } from "@repo/i18n"
import type {
	AdminShippingRate,
	CalibrationOriginInput,
	RunShippingCalibrationResponse,
} from "@repo/marketplace-admin"
import {
	computeShipsTo,
	getDefaultRates,
	getShippingZone,
	PACKAGE_SIZE_DEFINITIONS,
	PACKAGE_SIZES,
	type PackageSize,
	SENDCLOUD_ACTIVATED_COUNTRIES,
	type SendCloudActivatedCountry,
	SHIPPING_ZONES,
	type ShippingZone,
} from "@repo/shared-primitives"
import { createFileRoute } from "@tanstack/react-router"
import { Info, Settings2 } from "lucide-react"
import { useEffect, useMemo, useState } from "react"
import { toast } from "sonner"
import {
	PageHeader,
	Panel,
	Section,
	SectionStack,
	Toolbar,
} from "@/components/layout"
import { EstimateGrid } from "@/components/shipping-rates/estimate-grid"
import { WeightEmulator } from "@/components/shipping-rates/weight-emulator"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { MultiSelect } from "@/components/ui/multi-select"
import {
	Popover,
	PopoverContent,
	PopoverTrigger,
} from "@/components/ui/popover"
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "@/components/ui/select"
import { Skeleton } from "@/components/ui/skeleton"
import {
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from "@/components/ui/table"
import {
	Tooltip,
	TooltipContent,
	TooltipTrigger,
} from "@/components/ui/tooltip"
import {
	QuickFilter,
	QuickFilterGroup,
} from "@/features/moderation-admin/QuickFilter"
import {
	useDeleteShippingRate,
	useRunShippingCalibration,
	useShippingRates,
	useUpsertShippingRate,
} from "@/queries/marketplace"

export const Route = createFileRoute("/shipping-rates/")({
	component: ShippingRatesPage,
})

const ZONES = ["domestic", "region", "global"] as const
const VALUE_TIERS = [
	{
		value: "standard",
		short: "Standard",
		label: "Standard (≤ signature threshold)",
	},
	{
		value: "secured",
		short: "Secured",
		label: "Secured (signature, high value)",
	},
] as const
type Zone = (typeof ZONES)[number]
type Size = PackageSize
type ValueTier = (typeof VALUE_TIERS)[number]["value"]

// Display names for the SendCloud-activated countries. The code list is the
// single source of truth in @repo/shared-primitives — this record only supplies
// the label, and TypeScript requires an entry for every activated code, so admin
// and backend can't drift as the enum grows.
const ACTIVATED_COUNTRY_NAMES: Record<SendCloudActivatedCountry, string> = {
	FR: "France",
}
const ACTIVATED_COUNTRIES: Array<{ code: string; name: string }> =
	SENDCLOUD_ACTIVATED_COUNTRIES.map((code) => ({
		code,
		name: ACTIVATED_COUNTRY_NAMES[code],
	}))

// Parcel origin the calibration sweep quotes from, per activated country. Same
// compile-time completeness as the names above: activating a country fails the
// build until its origin is supplied.
const ORIGIN_DEFAULTS: Record<
	SendCloudActivatedCountry,
	CalibrationOriginInput
> = {
	FR: { postalCode: "75001", city: "Paris", line1: "1 rue de Rivoli" },
}

// Regional destinations worth quoting first: the largest market, then GB and CH
// which sit outside the EU customs union and price very differently.
const PREFERRED_REGION_DESTINATIONS = ["DE", "GB", "CH", "IT", "ES"]

/**
 * Destinations selected by default, per zone. Deliberately one each: every extra
 * destination multiplies the sweep by the number of parcel weights, and SendCloud
 * caps quote calls at 100 per minute, so a wide default run spends minutes
 * waiting. Widen it per zone when a spread within that zone is what you're after.
 */
const DEFAULT_DESTINATIONS_PER_ZONE = 1

/**
 * Mirrors the server's per-zone cap for display only. The limit is enforced in
 * the calibration service, which rejects a wider request with
 * TOO_MANY_DESTINATIONS; this just states it before a run is attempted.
 */
const MAX_DESTINATIONS_PER_ZONE = 8

type DestinationsByZone = Record<ShippingZone, string[]>

/**
 * Countries reachable from an origin, grouped by the zone they resolve to. Built
 * from `computeShipsTo`, so the picker can only offer routes a seller could
 * actually sell, and each country sits under the row it would be priced in.
 */
function shippableDestinations(country: string): DestinationsByZone {
	const byZone: DestinationsByZone = { domestic: [], region: [], global: [] }

	for (const destination of computeShipsTo(country, {
		domestic: true,
		region: true,
		global: true,
	})) {
		byZone[getShippingZone(country, destination)].push(destination)
	}

	return byZone
}

function defaultDestinations(country: string): DestinationsByZone {
	const available = shippableDestinations(country)
	const preferredRegion = PREFERRED_REGION_DESTINATIONS.filter((code) =>
		available.region.includes(code),
	)
	const region = preferredRegion.length > 0 ? preferredRegion : available.region

	return {
		domestic: available.domestic.slice(0, DEFAULT_DESTINATIONS_PER_ZONE),
		region: region.slice(0, DEFAULT_DESTINATIONS_PER_ZONE),
		global: available.global.slice(0, DEFAULT_DESTINATIONS_PER_ZONE),
	}
}

function keyFor(zone: Zone, size: Size): string {
	return `${zone}:${size}`
}

function ShippingRatesPage() {
	const [country, setCountry] = useState<string>(
		ACTIVATED_COUNTRIES[0]?.code ?? "FR",
	)
	const [valueTier, setValueTier] = useState<ValueTier>("standard")
	const { items, isLoading, error } = useShippingRates({ countryCode: country })
	const upsert = useUpsertShippingRate()
	const remove = useDeleteShippingRate()

	const calibration = useRunShippingCalibration()
	const [estimate, setEstimate] =
		useState<RunShippingCalibrationResponse | null>(null)
	const [origin, setOrigin] = useState<CalibrationOriginInput>(
		() => ORIGIN_DEFAULTS[country as SendCloudActivatedCountry],
	)
	const [destinations, setDestinations] = useState<DestinationsByZone>(() =>
		defaultDestinations(country),
	)

	const available = useMemo(() => shippableDestinations(country), [country])

	// A cell with no stored row is not free: the buyer pays the code default that
	// baseCountryRates lays the grid over. Show it so a blank input reads as
	// "using the default" rather than "unpriced".
	const codeDefaults = useMemo(() => getDefaultRates(country), [country])

	function selectCountry(next: string) {
		setCountry(next)
		// The two grids must always describe the same country.
		setEstimate(null)
		setOrigin(ORIGIN_DEFAULTS[next as SendCloudActivatedCountry])
		setDestinations(defaultDestinations(next))
	}

	function runCalibration() {
		calibration.mutate(
			{ countryCode: country, origin, destinations, hasRecipientPhone: true },
			{ onSuccess: setEstimate },
		)
	}

	// Local euro-string draft per cell, seeded from the loaded cents values for the
	// active tier. A NULL DB `valueTier` is the legacy standard cell. The hook
	// returns a stable `items` reference, so this effect runs only when the loaded
	// rows or the active tier change — not on every render.
	const [draft, setDraft] = useState<Record<string, string>>({})

	useEffect(() => {
		const next: Record<string, string> = {}
		for (const row of items as AdminShippingRate[]) {
			const rowTier = row.valueTier ?? "standard"
			if (rowTier !== valueTier) continue
			next[keyFor(row.zone as Zone, row.size as Size)] = (
				row.priceCents / 100
			).toFixed(2)
		}
		setDraft(next)
	}, [items, valueTier])

	const savingKey =
		upsert.isPending && upsert.variables
			? keyFor(upsert.variables.zone as Zone, upsert.variables.size as Size)
			: remove.isPending && remove.variables
				? keyFor(remove.variables.zone as Zone, remove.variables.size as Size)
				: null

	/** Whether a stored row exists for this cell in the active tier. */
	function hasStoredCell(zone: Zone, size: Size): boolean {
		return (items as AdminShippingRate[]).some(
			(r) =>
				r.zone === zone &&
				r.size === size &&
				(r.valueTier ?? "standard") === valueTier,
		)
	}

	function save(zone: Zone, size: Size) {
		const raw = (draft[keyFor(zone, size)] ?? "").trim()
		// Blank a Secured cell to clear the override → it falls back to the Standard
		// price. Only meaningful for the secured tier and when a row actually exists.
		if (raw === "") {
			if (valueTier === "secured" && hasStoredCell(zone, size)) {
				remove.mutate(
					{ countryCode: country, zone, size, valueTier },
					{
						onSuccess: () => {
							setEstimate(null)
							toast.success("Rate cleared")
						},
						onError: (error) =>
							toast.error(error.message || "Failed to clear the rate"),
					},
				)
			}
			return
		}
		const euros = Number.parseFloat(raw)
		if (!Number.isFinite(euros) || euros < 0) {
			toast.error("Enter a valid non-negative price")
			return
		}
		upsert.mutate(
			{
				countryCode: country,
				zone,
				size,
				valueTier,
				priceCents: Math.round(euros * 100),
			},
			{
				onSuccess: () => {
					// The estimate holds the grid prices as they were when it ran, so a
					// saved rate makes its comparison wrong.
					setEstimate(null)
					toast.success("Rate saved")
				},
				onError: (error) =>
					toast.error(error.message || "Failed to save the rate"),
			},
		)
	}

	const tierLabel =
		VALUE_TIERS.find((tier) => tier.value === valueTier)?.short ?? "Standard"

	return (
		<div className="flex h-[calc(100vh-3rem)] flex-col gap-6 overflow-auto p-6">
			<PageHeader
				title="Shipping rates"
				description={
					<span className="inline-flex flex-wrap items-center gap-1.5">
						Buyer shipping prices per SendCloud country, in euros per package
						size and zone. Edits apply immediately.
						<Tooltip>
							<TooltipTrigger asChild>
								<button
									type="button"
									aria-label="How the tiers and blank cells work"
									className="cursor-help text-meta hover:text-foreground"
								>
									<Info className="size-3.5" />
								</button>
							</TooltipTrigger>
							<TooltipContent className="max-w-sm">
								The Secured tier is charged when an order's value crosses the
								signature threshold (the carrier forces a signed service). A
								blank Secured cell falls back to the Standard price. A blank
								Standard cell is not free: buyers pay the greyed-out country
								default.
							</TooltipContent>
						</Tooltip>
					</span>
				}
				actions={
					<>
						<SweepSettings
							country={country}
							origin={origin}
							onOriginChange={(next) => {
								setEstimate(null)
								setOrigin(next)
							}}
							available={available}
							destinations={destinations}
							onDestinationsChange={(next) => {
								setEstimate(null)
								setDestinations(next)
							}}
						/>
						<Button
							onClick={runCalibration}
							disabled={calibration.isPending || !calibration.isAuthReady}
							title="Quotes every parcel weight against every destination. Paced under SendCloud's 100 calls per minute, so expect a minute or two."
						>
							{calibration.isPending
								? "Quoting… (1-2 min)"
								: "Estimate carrier cost"}
						</Button>
					</>
				}
			/>

			<Toolbar>
				<Select value={country} onValueChange={selectCountry}>
					<SelectTrigger className="w-56" aria-label="Country">
						<SelectValue placeholder="Select country" />
					</SelectTrigger>
					<SelectContent>
						{ACTIVATED_COUNTRIES.map((c) => (
							<SelectItem key={c.code} value={c.code}>
								{c.name} ({c.code})
							</SelectItem>
						))}
					</SelectContent>
				</Select>
				<QuickFilterGroup aria-label="Value tier">
					{VALUE_TIERS.map((tier) => (
						<QuickFilter
							key={tier.value}
							active={valueTier === tier.value}
							onClick={() => setValueTier(tier.value)}
							title={tier.label}
						>
							{tier.short}
						</QuickFilter>
					))}
				</QuickFilterGroup>
			</Toolbar>

			<SectionStack>
				<Section label={`Rates · ${tierLabel}`}>
					{error ? (
						<p className="text-bad">Error: {error.message}</p>
					) : isLoading ? (
						<div className="flex flex-col gap-1">
							{Array.from({ length: 4 }, (_, index) => (
								<Skeleton key={index} className="h-11 w-full" />
							))}
						</div>
					) : (
						<Panel>
							<Table>
								<TableHeader>
									<TableRow>
										<TableHead className="h-auto py-2 align-bottom">
											Zone
										</TableHead>
										{PACKAGE_SIZES.map((size) => (
											<TableHead
												key={size}
												className="h-auto py-2 align-bottom"
											>
												<span className="block font-medium text-foreground">
													{PACKAGE_SIZE_DEFINITIONS[size].shortLabel}
												</span>
												<span className="block font-normal">
													{PACKAGE_SIZE_DEFINITIONS[size].contents}
												</span>
											</TableHead>
										))}
									</TableRow>
								</TableHeader>
								<TableBody>
									{ZONES.map((zone) => (
										<TableRow key={zone}>
											<TableCell className="font-medium capitalize">
												{zone}
											</TableCell>
											{PACKAGE_SIZES.map((size) => {
												const k = keyFor(zone, size)
												const fallback = (
													codeDefaults[zone][size] / 100
												).toFixed(2)
												return (
													<TableCell key={size}>
														<div className="flex items-center gap-1">
															<div className="relative">
																<span className="absolute top-1/2 left-2 -translate-y-1/2 text-xs text-meta">
																	€
																</span>
																<Input
																	type="number"
																	min={0}
																	step={0.01}
																	value={draft[k] ?? ""}
																	placeholder={fallback}
																	title={`No override stored: buyers pay the ${country} default of €${fallback}.`}
																	onChange={(e) =>
																		setDraft((d) => ({
																			...d,
																			[k]: e.target.value,
																		}))
																	}
																	className="w-24 pl-5 text-right tabular-nums"
																/>
															</div>
															<Button
																size="sm"
																variant="ghost"
																disabled={savingKey === k}
																onClick={() => save(zone, size)}
															>
																{savingKey === k ? "Saving" : "Save"}
															</Button>
														</div>
													</TableCell>
												)
											})}
										</TableRow>
									))}
								</TableBody>
							</Table>
						</Panel>
					)}
				</Section>

				{(calibration.isPending || estimate) && (
					<Section
						label={`Estimated carrier cost · ${tierLabel}`}
						action={
							estimate && (
								<span className="text-xs text-meta tabular-nums">
									{estimate.quoteCount} lookups ·{" "}
									{estimate.destinations.domestic.length +
										estimate.destinations.region.length +
										estimate.destinations.global.length}{" "}
									destinations
								</span>
							)
						}
					>
						<div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-muted-foreground">
							<span>
								Live SendCloud quotes under the same rules a seller is offered.
								Click a cell for its orders.
							</span>
							<span className="flex items-center gap-1.5 text-xs">
								<span className="size-2.5 rounded-sm bg-bad-soft shadow-[inset_0_0_0_1px_var(--bad)]" />
								Costs more than we charge
							</span>
							<span className="flex items-center gap-1.5 text-xs">
								<span className="size-2.5 rounded-sm bg-warn-soft shadow-[inset_0_0_0_1px_var(--warn)]" />
								Same, but some option had no quote
							</span>
						</div>

						{calibration.isPending ? (
							<div className="flex flex-col gap-2">
								<p className="font-medium">
									This takes a minute or two. Leave the page open.
								</p>
								<p className="max-w-3xl text-muted-foreground">
									Every parcel weight is quoted separately against every
									destination, and SendCloud allows only 100 quote calls a
									minute. The sweep paces itself just under that ceiling on
									purpose: going faster gets the whole run rejected and costs
									about 50 seconds of waiting to recover. Fewer destinations
									means a shorter run.
								</p>
								<Skeleton className="h-64 w-full" />
							</div>
						) : estimate ? (
							<EstimateGrid result={estimate} tier={valueTier} />
						) : null}
					</Section>
				)}

				<WeightEmulator currency={getCurrencyForCountry(country) ?? "EUR"} />
			</SectionStack>
		</div>
	)
}

interface SweepSettingsProps {
	country: string
	origin: CalibrationOriginInput
	onOriginChange: (origin: CalibrationOriginInput) => void
	available: DestinationsByZone
	destinations: DestinationsByZone
	onDestinationsChange: (destinations: DestinationsByZone) => void
}

function SweepSettings({
	country,
	origin,
	onOriginChange,
	available,
	destinations,
	onDestinationsChange,
}: SweepSettingsProps) {
	return (
		<Popover>
			<PopoverTrigger asChild>
				<Button variant="outline">
					<Settings2 />
					Sweep settings
				</Button>
			</PopoverTrigger>
			<PopoverContent align="end" className="flex w-96 flex-col gap-4">
				<div className="space-y-2">
					<h4 className="font-medium">Ship from ({country})</h4>
					<div className="grid grid-cols-2 gap-2">
						<div className="space-y-1">
							<Label
								htmlFor="origin-postal"
								className="text-xs font-normal text-meta"
							>
								Postal code
							</Label>
							<Input
								id="origin-postal"
								value={origin.postalCode}
								onChange={(e) =>
									onOriginChange({ ...origin, postalCode: e.target.value })
								}
							/>
						</div>
						<div className="space-y-1">
							<Label
								htmlFor="origin-city"
								className="text-xs font-normal text-meta"
							>
								City
							</Label>
							<Input
								id="origin-city"
								value={origin.city ?? ""}
								onChange={(e) =>
									onOriginChange({ ...origin, city: e.target.value })
								}
							/>
						</div>
					</div>
					<div className="space-y-1">
						<Label
							htmlFor="origin-street"
							className="text-xs font-normal text-meta"
						>
							Street
						</Label>
						<Input
							id="origin-street"
							value={origin.line1 ?? ""}
							onChange={(e) =>
								onOriginChange({ ...origin, line1: e.target.value })
							}
						/>
					</div>
				</div>

				<div className="space-y-2">
					<h4 className="font-medium">Ship to</h4>
					{SHIPPING_ZONES.map((zone) => (
						<div key={zone} className="space-y-1">
							<Label className="text-xs font-normal text-meta capitalize">
								{zone}
							</Label>
							<MultiSelect
								key={`${country}:${zone}`}
								options={available[zone].map((code) => ({
									label: code,
									value: code,
								}))}
								defaultValue={destinations[zone]}
								onValueChange={(value) =>
									onDestinationsChange({ ...destinations, [zone]: value })
								}
								placeholder="No destinations"
								maxCount={8}
								hideSelectAll
							/>
						</div>
					))}
					<p className="text-xs text-meta">
						Only countries a seller in {country} can ship to, grouped by the
						zone they are priced in. Up to {MAX_DESTINATIONS_PER_ZONE} per zone.
						Each destination is quoted at every parcel weight, and SendCloud
						allows 100 quote calls a minute, so adding destinations makes the
						run noticeably longer.
					</p>
				</div>
			</PopoverContent>
		</Popover>
	)
}
